import { memo } from "react";

import type { AssistantFooterProgress, AssistantMessageProgress } from "../assistant-progress-state";
import type { ExtendedUIMessage, SuggestionInsertSlot } from "../chat.types";
import { getRenderKey } from "../render-identity";
import { AssistantChatSessionMessage } from "./assistant-chat-session-message";
import { UserChatSessionMessage } from "./user-chat-session-message";

export interface ChatSessionMessagesProps {
  messages: ExtendedUIMessage[];
  suggestionInsert?: SuggestionInsertSlot;
  assistantProgress?: AssistantFooterProgress;
}

/**
 * Groups consecutive messages by role to allow tighter spacing within assistant message runs.
 * This matches the streaming experience where consecutive assistant messages appear closer together.
 */
interface MessageGroup {
  role: "user" | "assistant" | "system";
  messages: ExtendedUIMessage[];
}

function groupMessagesByRole(messages: ExtendedUIMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];

  for (const message of messages) {
    const role = message.role;
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.role === role) {
      lastGroup.messages.push(message);
    } else {
      groups.push({ role, messages: [message] });
    }
  }

  return groups;
}

export const ChatSessionMessages = ({ messages, suggestionInsert, assistantProgress }: ChatSessionMessagesProps) => {
  const groups = groupMessagesByRole(messages);
  const renderMessage = (message: ExtendedUIMessage) => {
    const messageKey = getRenderKey(message);
    const progress =
      message.role === "assistant" && assistantProgress?.messageKey === messageKey
        ? assistantProgress.progress
        : undefined;

    return (
      <ChatSessionMessage key={messageKey} message={message} suggestionInsert={suggestionInsert} progress={progress} />
    );
  };

  return (
    <>
      {groups.map((group, groupIndex) => {
        // For assistant message groups with multiple messages, use tighter spacing
        if (group.role === "assistant" && group.messages.length > 1) {
          // Use negative margin on non-first articles to counteract internal content margins
          return (
            <div key={`group-${groupIndex}`} className="flex flex-col [&>article:not(:first-child)]:-mt-1">
              {group.messages.map(renderMessage)}
            </div>
          );
        }

        // Single messages or user messages render normally
        return group.messages.map(renderMessage);
      })}
    </>
  );
};

export interface ChatSessionMessageProps {
  message: ExtendedUIMessage;
  suggestionInsert?: SuggestionInsertSlot;
  progress?: AssistantMessageProgress;
}

const ChatSessionMessageRenderer = ({ message, suggestionInsert, progress }: ChatSessionMessageProps) => {
  switch (message.role) {
    case "user":
      return <UserChatSessionMessage message={message} />;

    case "assistant":
      return <AssistantChatSessionMessage message={message} suggestionInsert={suggestionInsert} progress={progress} />;

    default:
      return null;
  }
};

/**
 * Two slots carry the same callbacks. Call sites build the slot inline
 * (`suggestionInsert={{ onInsert }}`), so its object identity changes on every parent render even
 * though nothing about it did — comparing the callbacks instead is what lets a settled message hold
 * its memo.
 */
const isSameSuggestionInsert = (a: SuggestionInsertSlot | undefined, b: SuggestionInsertSlot | undefined): boolean =>
  a === b || (a?.onInsert === b?.onInsert && a?.extract === b?.extract);

/**
 * A settled turn's props are fully determined by its message object, which the render pipeline keeps
 * referentially stable: persisted rows come from one memoized adaptation of the history query, so a
 * row's identity changes only when the server actually re-sends it.
 *
 * Two things deliberately keep re-rendering. The live turn is rebuilt per frame by
 * `normalizeLiveTurn`, so its `message` is a new object on every streamed token. And the turn that
 * owns the progress footer gets a freshly-resolved `progress` object each render, so its label stays
 * live. Everything behind them — completed markdown, historical tool invocations — now holds.
 */
const areChatSessionMessagePropsEqual = (prev: ChatSessionMessageProps, next: ChatSessionMessageProps): boolean =>
  prev.message === next.message &&
  prev.progress === next.progress &&
  isSameSuggestionInsert(prev.suggestionInsert, next.suggestionInsert);

export const ChatSessionMessage = memo(ChatSessionMessageRenderer, areChatSessionMessagePropsEqual);
ChatSessionMessage.displayName = "ChatSessionMessage";
